import { FormEvent, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

const QUALIFICATION_VERSION = 'jlp-intake-v0.1';
const AUTO_REPLY_STATUS = 'future_capacity_gated';

type ChoiceOption = {
  value: string;
  label: string;
  description: string;
  score: number;
  tags: string[];
};

type FormState = {
  name: string;
  email: string;
  company: string;
  role: string;
  intent: string;
  intentOther: string;
  workContext: string[];
  workContextOther: string;
  desiredNextStep: string;
  urgency: string;
  readinessRating: number;
  clarityRating: number;
  decisionRole: string;
  message: string;
  sensitiveAck: boolean;
};

type SubmitStatus = 'idle' | 'submitting' | 'success' | 'error';
type QualificationTemperature = 'hot' | 'warm' | 'cool' | 'hold';
type LeadInquiryResponse = {
  ok?: boolean;
  lead_id?: string;
  deduped?: boolean;
  warning?: string;
  error?: string;
  continue_token?: string;
  continue_url?: string;
  continue_email_status?: 'sent' | 'failed' | 'skipped' | 'not_sent';
};

type LeadInterestSuccess = {
  name: string;
  email: string;
  leadId?: string;
  continueToken?: string;
  continueUrl?: string;
};

type LeadInterestFormProps = {
  onSuccess?: (submission: LeadInterestSuccess) => void;
};

type QualificationPayload = {
  qualification_version: string;
  qualification_answers: {
    intent_shape: string;
    intent_detail?: string;
    selected_work_context: string[];
    work_context_detail?: string;
    desired_next_step: string;
    urgency: string;
    readiness_rating: number;
    clarity_rating: number;
    decision_role: string;
    optional_note_provided: boolean;
    sender_context: SenderContext;
    sensitive_acknowledged: boolean;
    no_pricing_or_payment_commitment: true;
    auto_reply_status: typeof AUTO_REPLY_STATUS;
  };
  qualification_score: number;
  qualification_temperature: QualificationTemperature;
  qualification_tags: string[];
  recommended_route: string;
  recommended_next_action: string;
  missing_info: string[];
  sensitive_risk_flag: boolean;
  survey_completed_at: string;
};

type SenderContext = {
  timezone: string | null;
  timezone_offset_minutes: number | null;
  timezone_source: 'browser_intl' | 'manual_override' | 'unknown';
  locale: string | null;
  submitted_at_local: string | null;
  submitted_at_utc: string;
  capture_version: 'tz-capture-v0.1';
};

const initialForm: FormState = {
  name: '',
  email: '',
  company: '',
  role: '',
  intent: '',
  intentOther: '',
  workContext: [],
  workContextOther: '',
  desiredNextStep: '',
  urgency: 'exploring_fit',
  readinessRating: 3,
  clarityRating: 3,
  decisionRole: '',
  message: '',
  sensitiveAck: false,
};

const steps = [
  'Contact',
  'Need',
  'Fit',
  'Review',
] as const;

const intentOptions: ChoiceOption[] = [
  {
    value: 'cash_clarity',
    label: 'Cash clarity diagnostic',
    description: 'For operators who need the cash picture and books to tell the same story.',
    score: 22,
    tags: ['need:cash_clarity'],
  },
  {
    value: 'workflow_map',
    label: 'Finance workflow map',
    description: 'For split tools, unclear handoffs, or a process that needs sequencing before automation.',
    score: 20,
    tags: ['need:workflow_map'],
  },
  {
    value: 'cleanup_close',
    label: 'Cleanup or close rhythm',
    description: 'For month-end rhythm, evidence gaps, or cleanup order without sending records yet.',
    score: 18,
    tags: ['need:cleanup_close'],
  },
  {
    value: 'automation_prototype',
    label: 'Automation or prototype path',
    description: 'For a private build path after separate scope, payment, and data gates.',
    score: 16,
    tags: ['need:automation_prototype'],
  },
  {
    value: 'route_specialist',
    label: 'Route me to the right specialist',
    description: "For tax, legal, audit, filing, or provider work outside this public intake.",
    score: 5,
    tags: ['need:specialist_route'],
  },
  {
    value: 'other',
    label: 'Other',
    description: 'Use a short non-sensitive label; no names, records, credentials, or private files.',
    score: 8,
    tags: ['need:other'],
  },
];

const workContextOptions: ChoiceOption[] = [
  {
    value: 'split_systems',
    label: 'Finance picture is split across tools',
    description: 'Categories and tool names are enough. No screenshots or exports.',
    score: 10,
    tags: ['signal:split_systems'],
  },
  {
    value: 'cash_timing',
    label: 'Cash timing is hard to trust',
    description: 'Useful for a diagnostic map when cash, invoices, bills, or bank timing drift.',
    score: 10,
    tags: ['signal:cash_timing'],
  },
  {
    value: 'close_rhythm',
    label: 'Cleanup or close rhythm is unclear',
    description: 'Describe the rhythm, not the records. Private source files wait for secure-room review.',
    score: 8,
    tags: ['signal:close_rhythm'],
  },
  {
    value: 'reporting_drift',
    label: 'Reports do not match decisions',
    description: 'Tell us what decision is blocked, not the confidential details behind it.',
    score: 9,
    tags: ['signal:reporting_drift'],
  },
  {
    value: 'approval_bottleneck',
    label: 'Approvals or handoffs slow the work',
    description: 'Roles are enough. Do not include private names or employer/client details.',
    score: 7,
    tags: ['signal:approval_bottleneck'],
  },
  {
    value: 'statutory_advice',
    label: 'Tax, legal, audit, or statutory filing need',
    description: 'This usually routes elsewhere; the intake is not legal, tax, or audit counsel.',
    score: -12,
    tags: ['signal:specialist_needed'],
  },
  {
    value: 'other',
    label: 'Other',
    description: 'Keep it high-level and public-safe.',
    score: 3,
    tags: ['signal:other'],
  },
];

const nextStepOptions: ChoiceOption[] = [
  {
    value: 'written_prefit',
    label: 'Written prefit review',
    description: 'Best first move when the shape is clear enough for async review.',
    score: 8,
    tags: ['outcome:written_prefit'],
  },
  {
    value: 'diagnostic_map',
    label: 'Diagnostic map if fit is strong',
    description: 'No price, payment, or scope is promised by choosing this.',
    score: 14,
    tags: ['outcome:diagnostic_map'],
  },
  {
    value: 'build_sprint_later',
    label: 'Build sprint or prototype later',
    description: 'Only after a separate scope, payment, and data-handling gate.',
    score: 12,
    tags: ['outcome:build_later'],
  },
  {
    value: 'route_specialist',
    label: 'Route to a specialist',
    description: 'Useful when the right answer is tax, legal, audit, or platform-provider support.',
    score: 5,
    tags: ['outcome:specialist_route'],
  },
  {
    value: 'not_sure',
    label: 'Not sure yet',
    description: 'Still acceptable; the intake can recommend the next review lane.',
    score: 6,
    tags: ['outcome:not_sure'],
  },
];

const urgencyOptions: ChoiceOption[] = [
  {
    value: 'this_month',
    label: 'This month',
    description: 'There is active decision pressure, but still no emergency data intake.',
    score: 16,
    tags: ['urgency:high'],
  },
  {
    value: 'next_30_60_days',
    label: 'Next 30-60 days',
    description: 'Useful timing for async fit, scope, and capacity review.',
    score: 11,
    tags: ['urgency:medium'],
  },
  {
    value: 'exploring_fit',
    label: 'Exploring calmly',
    description: 'Discovery mode with no immediate implementation pressure.',
    score: 6,
    tags: ['urgency:low'],
  },
  {
    value: 'not_urgent',
    label: 'Not urgent',
    description: 'Good for product trust questions or future planning.',
    score: 3,
    tags: ['urgency:low'],
  },
];

const decisionRoleOptions: ChoiceOption[] = [
  {
    value: 'decision_owner',
    label: 'I can approve next steps',
    description: 'You can approve a diagnostic, workspace review, or scoped follow-up if fit is strong.',
    score: 12,
    tags: ['authority:approver'],
  },
  {
    value: 'finance_operator',
    label: 'I run or own the finance workflow',
    description: 'You may not be the payer, but you understand the books, tools, and pain points.',
    score: 10,
    tags: ['authority:operator'],
  },
  {
    value: 'founder_delegated',
    label: 'Founder or leadership asked me to explore',
    description: 'Useful signal, especially if the approver role is clear later.',
    score: 8,
    tags: ['authority:delegated'],
  },
  {
    value: 'researching',
    label: 'I am researching options',
    description: 'Early fit can still be reviewed, but a decision owner may be needed.',
    score: 4,
    tags: ['authority:researching'],
  },
];

const sensitiveTerms = [
  'api key',
  'bank statement',
  'client list',
  'confidential',
  'credential',
  'customer list',
  'employer',
  'password',
  'payroll',
  'private file',
  'screenshot',
  'tax return',
  'vendor list',
];

function optionLabel(options: ChoiceOption[], value: string) {
  return options.find((option) => option.value === value)?.label || value;
}

function getSelectedOption(options: ChoiceOption[], value: string) {
  return options.find((option) => option.value === value);
}

function clampScore(score: number) {
  return Math.max(0, Math.min(100, Math.round(score)));
}

function detectSensitiveRisk(message: string) {
  const normalized = message.toLowerCase();
  return sensitiveTerms.some((term) => normalized.includes(term));
}

function activeIntentLabel(form: FormState) {
  if (form.intent === 'other' && form.intentOther.trim()) {
    return `Other: ${form.intentOther.trim()}`;
  }
  return optionLabel(intentOptions, form.intent);
}

function selectedWorkContextLabels(form: FormState) {
  return form.workContext.map((value) => {
    if (value === 'other' && form.workContextOther.trim()) {
      return `Other: ${form.workContextOther.trim()}`;
    }
    return optionLabel(workContextOptions, value);
  });
}

function buildTags(form: FormState, temperature: QualificationTemperature, route: string) {
  const tags = new Set<string>([
    'source:jlpatawaran.com',
    `qualification:${QUALIFICATION_VERSION}`,
    `temperature:${temperature}`,
    `route:${route}`,
  ]);

  [
    getSelectedOption(intentOptions, form.intent),
    getSelectedOption(nextStepOptions, form.desiredNextStep),
    getSelectedOption(urgencyOptions, form.urgency),
    getSelectedOption(decisionRoleOptions, form.decisionRole),
  ].forEach((option) => option?.tags.forEach((tag) => tags.add(tag)));

  form.workContext.forEach((value) => {
    getSelectedOption(workContextOptions, value)?.tags.forEach((tag) => tags.add(tag));
  });

  if (detectSensitiveRisk(form.message)) {
    tags.add('risk:sensitive-language');
  }

  if (!form.message.trim()) {
    tags.add('note:none');
  }

  return Array.from(tags);
}

function recommendedRoute(form: FormState, sensitiveRiskFlag: boolean) {
  if (sensitiveRiskFlag) return 'manual_sensitive_review';

  if (
    form.intent === 'route_specialist' ||
    form.desiredNextStep === 'route_specialist' ||
    form.workContext.includes('statutory_advice')
  ) {
    return 'specialist_routing_review';
  }

  if (form.desiredNextStep === 'written_prefit') return 'async_prefit_review';
  if (form.desiredNextStep === 'diagnostic_map') return 'diagnostic_prefit';
  if (form.desiredNextStep === 'build_sprint_later' || form.intent === 'automation_prototype') {
    return 'build_sprint_prefit';
  }

  return 'manual_prefit_review';
}

function recommendedNextAction(route: string, temperature: QualificationTemperature) {
  const suffix =
    'Manual review only. Auto-replies are future/capacity-gated and are not active on this intake.';

  if (route === 'manual_sensitive_review') {
    return `Review for possible sensitive-data cleanup before any next step. ${suffix}`;
  }

  if (temperature === 'hot') {
    return `Prioritize fit review and reply with the strongest route if capacity exists. ${suffix}`;
  }

  if (temperature === 'warm') {
    return `Review for diagnostic, advisory, collaboration, or referral fit. ${suffix}`;
  }

  return `Hold for manual triage; ask for missing context only if the signal is real. ${suffix}`;
}

function missingInfo(form: FormState) {
  const missing: string[] = [];

  if (!form.company.trim()) missing.push('Company or project name');
  if (!form.role.trim()) missing.push('Sender role');
  if (form.intent === 'other' && !form.intentOther.trim()) missing.push('Other need');
  if (form.workContext.includes('other') && !form.workContextOther.trim()) {
    missing.push('Other work context');
  }
  if (form.clarityRating <= 2) missing.push('Clearer stuck decision or workflow');
  if (form.readinessRating <= 2) missing.push('What would make timing real');
  if (!form.message.trim()) missing.push('Optional non-sensitive note');

  return missing;
}

function captureSenderContext(now: Date = new Date()): SenderContext {
  try {
    const resolved = Intl.DateTimeFormat().resolvedOptions();
    const timezone = resolved.timeZone || null;
    const offsetMinutes = -now.getTimezoneOffset();
    return {
      timezone,
      timezone_offset_minutes: Number.isFinite(offsetMinutes) ? offsetMinutes : null,
      timezone_source: timezone ? 'browser_intl' : 'unknown',
      locale: typeof navigator !== 'undefined' ? navigator.language || null : null,
      submitted_at_local: formatLocalIsoWithOffset(now),
      submitted_at_utc: now.toISOString(),
      capture_version: 'tz-capture-v0.1',
    };
  } catch {
    return {
      timezone: null,
      timezone_offset_minutes: null,
      timezone_source: 'unknown',
      locale: null,
      submitted_at_local: null,
      submitted_at_utc: now.toISOString(),
      capture_version: 'tz-capture-v0.1',
    };
  }
}

function formatLocalIsoWithOffset(date: Date): string {
  const pad = (value: number) => String(value).padStart(2, '0');
  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hour = pad(date.getHours());
  const minute = pad(date.getMinutes());
  const second = pad(date.getSeconds());
  const offsetMinutes = -date.getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const absoluteOffset = Math.abs(offsetMinutes);
  const offsetHours = pad(Math.floor(absoluteOffset / 60));
  const offsetRemainder = pad(absoluteOffset % 60);

  return `${year}-${month}-${day}T${hour}:${minute}:${second}${sign}${offsetHours}:${offsetRemainder}`;
}

function buildQualification(form: FormState, completedAt: Date = new Date()): QualificationPayload {
  const sensitiveRiskFlag = detectSensitiveRisk(form.message);
  const intentScore = getSelectedOption(intentOptions, form.intent)?.score || 0;
  const contextScore = form.workContext.reduce(
    (total, value) => total + (getSelectedOption(workContextOptions, value)?.score || 0),
    0
  );
  const nextStepScore = getSelectedOption(nextStepOptions, form.desiredNextStep)?.score || 0;
  const urgencyScore = getSelectedOption(urgencyOptions, form.urgency)?.score || 0;
  const decisionScore = getSelectedOption(decisionRoleOptions, form.decisionRole)?.score || 0;
  const noteScore = form.message.trim().length >= 40 ? 6 : form.message.trim().length > 0 ? 3 : 0;

  const score = clampScore(
    intentScore +
      Math.min(contextScore, 20) +
      nextStepScore +
      urgencyScore +
      decisionScore +
      form.readinessRating * 5 +
      form.clarityRating * 4 +
      noteScore -
      (sensitiveRiskFlag ? 12 : 0)
  );

  const temperature: QualificationTemperature = sensitiveRiskFlag
    ? score >= 70
      ? 'warm'
      : 'hold'
    : score >= 76
      ? 'hot'
      : score >= 52
        ? 'warm'
        : 'cool';

  const route = recommendedRoute(form, sensitiveRiskFlag);

  return {
    qualification_version: QUALIFICATION_VERSION,
    qualification_answers: {
      intent_shape: activeIntentLabel(form),
      intent_detail: form.intent === 'other' ? form.intentOther.trim() || undefined : undefined,
      selected_work_context: selectedWorkContextLabels(form),
      work_context_detail: form.workContext.includes('other')
        ? form.workContextOther.trim() || undefined
        : undefined,
      desired_next_step: optionLabel(nextStepOptions, form.desiredNextStep),
      urgency: optionLabel(urgencyOptions, form.urgency),
      readiness_rating: form.readinessRating,
      clarity_rating: form.clarityRating,
      decision_role: optionLabel(decisionRoleOptions, form.decisionRole),
      optional_note_provided: Boolean(form.message.trim()),
      sender_context: captureSenderContext(completedAt),
      sensitive_acknowledged: form.sensitiveAck,
      no_pricing_or_payment_commitment: true,
      auto_reply_status: AUTO_REPLY_STATUS,
    },
    qualification_score: score,
    qualification_temperature: temperature,
    qualification_tags: buildTags(form, temperature, route),
    recommended_route: route,
    recommended_next_action: recommendedNextAction(route, temperature),
    missing_info: missingInfo(form),
    sensitive_risk_flag: sensitiveRiskFlag,
    survey_completed_at: completedAt.toISOString(),
  };
}

function formatQualificationSummary(qualification: QualificationPayload) {
  return [
    `Qualification version: ${qualification.qualification_version}`,
    `Score: ${qualification.qualification_score}`,
    `Temperature: ${qualification.qualification_temperature}`,
    `Route: ${qualification.recommended_route}`,
    `Next action: ${qualification.recommended_next_action}`,
    `Tags: ${qualification.qualification_tags.join(', ') || 'None'}`,
    `Missing info: ${qualification.missing_info.join(', ') || 'None'}`,
    `Sensitive risk flag: ${qualification.sensitive_risk_flag ? 'yes' : 'no'}`,
  ].join('\n');
}

function buildMailto(form: FormState, qualification: QualificationPayload) {
  const subject = `Interest note: ${activeIntentLabel(form) || 'JLP intake'}`;
  const source =
    typeof window !== 'undefined'
      ? `${window.location.origin}${window.location.pathname}`
      : '/work-with-me';
  const body = [
    'Hi JL,',
    '',
    'I would like to raise interest in working together.',
    '',
    `Name: ${form.name}`,
    `Email: ${form.email}`,
    `Company/project: ${form.company || 'Not provided'}`,
    `Role: ${form.role || 'Not provided'}`,
    `Primary need: ${activeIntentLabel(form)}`,
    `Fit signals: ${selectedWorkContextLabels(form).join(', ')}`,
    `Desired outcome: ${optionLabel(nextStepOptions, form.desiredNextStep)}`,
    `Urgency: ${optionLabel(urgencyOptions, form.urgency)}`,
    `Readiness: ${form.readinessRating}/5`,
    `Decision role: ${optionLabel(decisionRoleOptions, form.decisionRole)}`,
    `Source: ${source}`,
    '',
    'Qualification summary:',
    formatQualificationSummary(qualification),
    '',
    'Optional non-sensitive note:',
    form.message.trim() || 'No optional note provided.',
    '',
    'Acknowledgement: I have not included credentials, private files, confidential records, raw financial exports, customer/vendor lists, payroll or tax records, employer/client private details, or screenshots with sensitive data.',
  ].join('\n');

  return `mailto:jl@jlpatawaran.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

function hasUsableEmail(value: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

function HelpText({ id, children }: { id: string; children: string }) {
  return (
    <p id={id} className="flex gap-2 text-xs leading-relaxed text-ink/55">
      <span
        className="mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-ink/20 text-[10px] font-semibold text-ink/60"
        title={children}
        aria-label={`Hint: ${children}`}
      >
        ?
      </span>
      <span>{children}</span>
    </p>
  );
}

function QuestionHeader({
  title,
  help,
  helpId,
}: {
  title: string;
  help: string;
  helpId: string;
}) {
  return (
    <div className="grid gap-2">
      <h3 className="font-display text-2xl tracking-tight text-ink">{title}</h3>
      <HelpText id={helpId}>{help}</HelpText>
    </div>
  );
}

function ChoiceCard({
  option,
  selected,
  onClick,
  multi = false,
}: {
  option: ChoiceOption;
  selected: boolean;
  onClick: () => void;
  multi?: boolean;
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      title={option.description}
      onClick={onClick}
      className={cn(
        'grid grid-cols-[1fr_auto] gap-x-4 gap-y-1 rounded-sm border p-3 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        selected
          ? 'border-ink bg-ink text-paper shadow-[0_16px_40px_rgba(11,13,16,0.12)]'
          : 'border-ink/10 bg-white text-ink hover:border-ink/30 hover:bg-ink/[0.02]'
      )}
    >
      <span className="min-w-0 text-sm font-semibold leading-snug">{option.label}</span>
      <span
        aria-hidden="true"
        className={cn(
          'inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-sm border text-[11px] font-bold',
          selected ? 'border-paper bg-paper text-ink' : 'border-ink/20 text-transparent'
        )}
      >
        {selected ? '✓' : ''}
      </span>
      <span className={cn('col-span-2 text-xs leading-relaxed', selected ? 'text-paper/75' : 'text-ink/55')}>
        {option.description}
      </span>
      <span className="sr-only">
        {multi ? 'Checkbox style option.' : 'Single choice option.'}
      </span>
    </button>
  );
}

function SingleChoiceGroup({
  label,
  help,
  helpId,
  options,
  value,
  onChange,
  columns = 'md:grid-cols-2',
}: {
  label: string;
  help: string;
  helpId: string;
  options: ChoiceOption[];
  value: string;
  onChange: (value: string) => void;
  columns?: string;
}) {
  return (
    <fieldset className="grid gap-4" aria-describedby={helpId}>
      <legend className="sr-only">{label}</legend>
      <div className="grid gap-2">
        <p className="text-sm font-medium text-ink">{label}</p>
        <HelpText id={helpId}>{help}</HelpText>
      </div>
      <div className={cn('grid gap-3', columns)}>
        {options.map((option) => (
          <ChoiceCard
            key={option.value}
            option={option}
            selected={value === option.value}
            onClick={() => onChange(option.value)}
          />
        ))}
      </div>
    </fieldset>
  );
}

function MultiChoiceGroup({
  label,
  help,
  helpId,
  options,
  value,
  onChange,
}: {
  label: string;
  help: string;
  helpId: string;
  options: ChoiceOption[];
  value: string[];
  onChange: (value: string[]) => void;
}) {
  function toggle(nextValue: string) {
    if (value.includes(nextValue)) {
      onChange(value.filter((item) => item !== nextValue));
      return;
    }
    onChange([...value, nextValue]);
  }

  return (
    <fieldset className="grid gap-4" aria-describedby={helpId}>
      <legend className="sr-only">{label}</legend>
      <div className="grid gap-2">
        <p className="text-sm font-medium text-ink">{label}</p>
        <HelpText id={helpId}>{help}</HelpText>
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        {options.map((option) => (
          <ChoiceCard
            key={option.value}
            option={option}
            selected={value.includes(option.value)}
            onClick={() => toggle(option.value)}
            multi
          />
        ))}
      </div>
    </fieldset>
  );
}

function RatingGroup({
  label,
  help,
  helpId,
  value,
  onChange,
  lowLabel,
  highLabel,
}: {
  label: string;
  help: string;
  helpId: string;
  value: number;
  onChange: (value: number) => void;
  lowLabel: string;
  highLabel: string;
}) {
  return (
    <fieldset className="grid gap-3" aria-describedby={helpId}>
      <legend className="text-sm font-medium text-ink">{label}</legend>
      <HelpText id={helpId}>{help}</HelpText>
      <div className="grid grid-cols-5 gap-2">
        {[1, 2, 3, 4, 5].map((rating) => (
          <button
            key={rating}
            type="button"
            aria-pressed={value === rating}
            title={`${label}: ${rating} of 5`}
            onClick={() => onChange(rating)}
            className={cn(
              'h-11 rounded-sm border text-sm font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              value === rating
                ? 'border-ink bg-ink text-paper'
                : 'border-ink/15 bg-white text-ink hover:border-ink/35'
            )}
          >
            {rating}
          </button>
        ))}
      </div>
      <div className="flex justify-between text-xs text-ink/50">
        <span>{lowLabel}</span>
        <span>{highLabel}</span>
      </div>
    </fieldset>
  );
}

export function LeadInterestForm({ onSuccess }: LeadInterestFormProps = {}) {
  const [form, setForm] = useState<FormState>(initialForm);
  const [stepIndex, setStepIndex] = useState(0);
  const [attempted, setAttempted] = useState(false);
  const [stepAttempted, setStepAttempted] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>('idle');
  const [submitMessage, setSubmitMessage] = useState<string | null>(null);

  const qualification = useMemo(() => buildQualification(form), [form]);
  const mailto = useMemo(() => buildMailto(form, qualification), [form, qualification]);

  const stepReady = [
    form.name.trim().length > 0 && hasUsableEmail(form.email) && form.company.trim().length > 0,
    form.workContext.length > 0 &&
      (!form.workContext.includes('other') || form.workContextOther.trim().length > 0) &&
      Boolean(form.intent) &&
      (form.intent !== 'other' || form.intentOther.trim().length > 0),
    Boolean(form.urgency) &&
      form.readinessRating > 0 &&
      Boolean(form.decisionRole) &&
      Boolean(form.desiredNextStep),
    form.sensitiveAck,
  ];

  const isReady = stepReady.every(Boolean);
  const sensitiveWarning = detectSensitiveRisk(form.message);

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (submitStatus !== 'idle') {
      setSubmitStatus('idle');
      setSubmitMessage(null);
    }
  }

  function goNext() {
    setStepAttempted(true);
    if (!stepReady[stepIndex]) return;
    setStepAttempted(false);
    setStepIndex((current) => Math.min(current + 1, steps.length - 1));
  }

  function goBack() {
    setStepAttempted(false);
    setStepIndex((current) => Math.max(current - 1, 0));
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (stepIndex < steps.length - 1) {
      goNext();
      return;
    }

    setAttempted(true);
    if (!isReady) {
      return;
    }

    setSubmitStatus('submitting');

    const sourceUrl =
      typeof window !== 'undefined'
        ? `${window.location.origin}${window.location.pathname}`
        : '/work-with-me';
    const submittedQualification = buildQualification(form, new Date());

    try {
      const response = await fetch('/api/lead-inquiry', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          company: form.company.trim() || undefined,
          role: form.role.trim() || undefined,
          intent_shape: activeIntentLabel(form),
          urgency: form.urgency,
          message: form.message.trim(),
          sensitive_data_ack: form.sensitiveAck,
          qualification_version: submittedQualification.qualification_version,
          qualification_answers: submittedQualification.qualification_answers,
          qualification_score: submittedQualification.qualification_score,
          qualification_temperature: submittedQualification.qualification_temperature,
          qualification_tags: submittedQualification.qualification_tags,
          recommended_route: submittedQualification.recommended_route,
          recommended_next_action: submittedQualification.recommended_next_action,
          missing_info: submittedQualification.missing_info,
          sensitive_risk_flag: submittedQualification.sensitive_risk_flag,
          survey_completed_at: submittedQualification.survey_completed_at,
          source_route: typeof window !== 'undefined' ? window.location.pathname : '/work-with-me',
          source_url: sourceUrl,
        }),
      });

      const body = (await response.json().catch(() => ({}))) as LeadInquiryResponse;

      if (!response.ok) {
        throw new Error(body.error || 'Lead inquiry submission failed');
      }

      setSubmitStatus('success');
      setSubmitMessage(
        body.deduped
          ? 'Already received. This same async prefit is already in the intake queue, so I did not create a second copy.'
          : body.warning ||
              'Received. JL will review the structured note and reply with the right next step if there is fit and capacity.',
      );
      onSuccess?.({
        name: form.name.trim(),
        email: form.email.trim(),
        leadId: body.lead_id,
        continueToken: body.continue_token,
        continueUrl: body.continue_url,
      });
    } catch {
      setSubmitStatus('error');
    }
  }

  if (submitStatus === 'success') {
    return (
      <div className="rounded-sm border border-ink/10 bg-white p-8 shadow-[0_18px_60px_rgba(11,13,16,0.06)]">
        <p className="font-display text-xs uppercase tracking-[0.22em] text-accent">
          {submitMessage?.startsWith('Already received') ? 'Already received' : 'Received'}
        </p>
        <h3 className="mt-4 font-display text-2xl tracking-tight">
          {submitMessage?.startsWith('Already received')
            ? 'No duplicate copy created.'
            : 'Async prefit received.'}
        </h3>
        <p className="mt-4 text-sm leading-relaxed text-ink/70">
          {submitMessage ||
            'JL will review the structured note and reply with the right next step if there is fit and capacity.'}
        </p>
        <p className="mt-4 text-sm text-ink/55">
          The submit path is closed for this form state. Use the optional Stage 2 note below only if you want to add sharper public-safe context.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-sm border border-ink/10 bg-white shadow-[0_18px_60px_rgba(11,13,16,0.06)]"
    >
      <div className="border-b border-ink/10 p-5 sm:p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-accent">
              Guided qualification
            </p>
            <p className="mt-2 font-display text-2xl tracking-tight text-ink">
              Step {stepIndex + 1} of {steps.length}: {steps[stepIndex]}
            </p>
          </div>
          <div className="rounded-sm border border-ink/10 bg-paper px-3 py-2 text-xs text-ink/60">
            Score preview: {qualification.qualification_score} / {qualification.qualification_temperature}
          </div>
        </div>
        <div className="mt-5 grid grid-cols-5 gap-2" aria-hidden="true">
          {steps.map((step, index) => (
            <span
              key={step}
              className={cn(
                'h-1.5 rounded-full',
                index <= stepIndex ? 'bg-ink' : 'bg-ink/10'
              )}
            />
          ))}
        </div>
      </div>

      <div className="grid gap-6 p-5 sm:p-8">
        {stepIndex === 0 && (
          <section className="grid gap-6">
            <QuestionHeader
              title="Who should the reply go to?"
              help="Use business contact details only. Private account links, credentials, and client records do not belong here."
              helpId="lead-contact-help"
            />
            <div className="grid gap-5 sm:grid-cols-2">
              <div className="grid gap-2">
                <Label htmlFor="lead-name">Name</Label>
                <Input
                  id="lead-name"
                  required
                  aria-describedby="lead-name-help"
                  value={form.name}
                  onChange={(event) => update('name', event.target.value)}
                />
                <HelpText id="lead-name-help">A real name helps route the reply cleanly.</HelpText>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="lead-email">Email</Label>
                <Input
                  id="lead-email"
                  type="email"
                  required
                  aria-describedby="lead-email-help"
                  value={form.email}
                  onChange={(event) => update('email', event.target.value)}
                />
                <HelpText id="lead-email-help">Use a reachable work email when possible.</HelpText>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="lead-company">Company or project</Label>
                <Input
                  id="lead-company"
                  required
                  aria-describedby="lead-company-help"
                  value={form.company}
                  onChange={(event) => update('company', event.target.value)}
                />
                <HelpText id="lead-company-help">
                  A public business or project label is enough. Do not list confidential client names.
                </HelpText>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="lead-role">Role</Label>
                <Input
                  id="lead-role"
                  aria-describedby="lead-role-help"
                  value={form.role}
                  onChange={(event) => update('role', event.target.value)}
                />
                <HelpText id="lead-role-help">Role-level context is enough. No private names are needed.</HelpText>
              </div>
            </div>
          </section>
        )}

        {stepIndex === 1 && (
          <section className="grid gap-7">
            <QuestionHeader
              title="What shape should the review inspect?"
              help="These choices create the structured qualification payload and keep the free-text box light."
              helpId="lead-shape-help"
            />
            <SingleChoiceGroup
              label="What should the review inspect first?"
              help="Choose the closest commercial need. The route can still change after operator review."
              helpId="lead-intent-help"
              options={intentOptions}
              value={form.intent}
              onChange={(value) => update('intent', value)}
            />
            {form.intent === 'other' && (
              <div className="grid gap-2">
                <Label htmlFor="lead-intent-other">Other need</Label>
                <Input
                  id="lead-intent-other"
                  aria-describedby="lead-intent-other-help"
                  value={form.intentOther}
                  onChange={(event) => update('intentOther', event.target.value)}
                />
                <HelpText id="lead-intent-other-help">
                  One short public-safe phrase is enough. No private records or pricing details.
                </HelpText>
              </div>
            )}
            <MultiChoiceGroup
              label="Which fit signals are present?"
              help="Check the public-safe patterns, not the private evidence. Tool categories and role-level descriptions are enough."
              helpId="lead-context-help"
              options={workContextOptions}
              value={form.workContext}
              onChange={(value) => update('workContext', value)}
            />
            {form.workContext.includes('other') && (
              <div className="grid gap-2">
                <Label htmlFor="lead-context-other">Other signal</Label>
                <Input
                  id="lead-context-other"
                  aria-describedby="lead-context-other-help"
                  value={form.workContextOther}
                  onChange={(event) => update('workContextOther', event.target.value)}
                />
                <HelpText id="lead-context-other-help">
                  Keep it general: the type of workflow or decision, not the private data behind it.
                </HelpText>
              </div>
            )}
          </section>
        )}

        {stepIndex === 2 && (
          <section className="grid gap-7">
            <QuestionHeader
              title="How real is the timing and authority?"
              help="Timing, authority, and readiness help choose hold, decline, route, prefit, or product review."
              helpId="lead-timing-help"
            />
            <SingleChoiceGroup
              label="Urgency"
              help="Choose the option that best describes the decision window."
              helpId="lead-urgency-help"
              options={urgencyOptions}
              value={form.urgency}
              onChange={(value) => update('urgency', value)}
              columns="md:grid-cols-2"
            />
            <SingleChoiceGroup
              label="Decision role"
              help="This helps route replies without asking for budget, pricing, or payment details."
              helpId="lead-decision-role-help"
              options={decisionRoleOptions}
              value={form.decisionRole}
              onChange={(value) => update('decisionRole', value)}
            />
            <RatingGroup
              label="Readiness rating"
              help="A routing signal for operator review, not acceptance or pricing."
              helpId="lead-readiness-help"
              value={form.readinessRating}
              onChange={(value) => update('readinessRating', value)}
              lowLabel="early"
              highLabel="ready"
            />
            <SingleChoiceGroup
              label="What outcome would be useful?"
              help="No price, payment, or scope is promised by this selection."
              helpId="lead-next-step-help"
              options={nextStepOptions}
              value={form.desiredNextStep}
              onChange={(value) => update('desiredNextStep', value)}
            />
          </section>
        )}

        {stepIndex === 3 && (
          <section className="grid gap-6">
            <QuestionHeader
              title="Add only public-safe context."
              help="Use the smallest useful public-safe story. The form intentionally blocks private-data intake."
              helpId="lead-review-help"
            />
            <div className="grid gap-3 rounded-sm border border-ink/10 bg-paper p-4 text-sm text-ink/70">
              <div className="flex justify-between gap-4">
                <span>Route</span>
                <strong className="text-right text-ink">{qualification.recommended_route}</strong>
              </div>
              <div className="flex justify-between gap-4">
                <span>Temperature</span>
                <strong className="text-right text-ink">{qualification.qualification_temperature}</strong>
              </div>
              <div className="flex justify-between gap-4">
                <span>Missing info</span>
                <strong className="text-right text-ink">
                  {qualification.missing_info.length ? qualification.missing_info.join(', ') : 'None'}
                </strong>
              </div>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="lead-message">Non-sensitive context</Label>
              <Textarea
                id="lead-message"
                value={form.message}
                onChange={(event) => update('message', event.target.value)}
                placeholder="Add a short public-safe detail: the decision, workflow, constraint, or useful next step. No files, screenshots, private values, or confidential names."
                aria-describedby="lead-message-help"
                className="min-h-36"
              />
              <HelpText id="lead-message-help">
                General workflow context is useful. Credentials, private files, raw exports, client/vendor lists, payroll, tax records, and confidential employer or client details are not.
              </HelpText>
              {sensitiveWarning && (
                <p className="rounded-sm border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
                  Sensitive-language check triggered. Keep the note general and remove private records,
                  names, values, screenshots, or credentials before sending.
                </p>
              )}
            </div>

            <label className="flex gap-3 rounded-sm border border-ink/10 bg-paper p-4 text-sm leading-relaxed text-ink/75">
              <input
                type="checkbox"
                checked={form.sensitiveAck}
                onChange={(event) => update('sensitiveAck', event.target.checked)}
                className="mt-1 h-4 w-4 shrink-0 accent-ink"
              />
              <span>
                I understand this is a public-safe first note. I will not include
                credentials, API keys, bank records, payroll, tax records, customer or
                vendor lists, confidential employer/client details, private files, or
                screenshots containing sensitive data. I also understand this intake
                does not create a pricing, payment, legal, tax, or advisory commitment.
              </span>
            </label>

            <p className="text-sm text-ink/60">
              JL reviews submissions manually. Auto-replies are future/capacity-gated and are
              not active on this intake.
            </p>
          </section>
        )}

        {stepAttempted && !stepReady[stepIndex] && (
          <p className="rounded-sm border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
            Complete the required choices on this step before moving forward.
          </p>
        )}

        {attempted && !isReady && (
          <p className="rounded-sm border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
            Add your required choices and confirm the sensitive-data acknowledgement before sending.
          </p>
        )}

        {submitStatus === 'error' && (
          <p className="rounded-sm border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
            Submission did not go through. You can{' '}
            <a className="underline underline-offset-4" href={mailto}>
              draft an email fallback
            </a>
            .
          </p>
        )}
      </div>

      <div className="flex flex-col gap-3 border-t border-ink/10 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6">
        <Button
          type="button"
          variant="outline"
          onClick={goBack}
          disabled={stepIndex === 0 || submitStatus === 'submitting'}
          className="w-full sm:w-auto"
        >
          Back
        </Button>
        <div className="flex flex-col gap-3 sm:flex-row">
          {stepIndex < steps.length - 1 ? (
            <Button type="button" onClick={goNext} className="w-full sm:w-auto">
              Continue
            </Button>
          ) : (
            <Button
              type="submit"
              size="lg"
              disabled={submitStatus === 'submitting'}
              className="w-full sm:w-auto"
            >
              {submitStatus === 'submitting' ? 'Sending...' : 'Send guided note'}
            </Button>
          )}
        </div>
      </div>
    </form>
  );
}
